import { useEffect, useMemo, useState } from 'react';
import { useCollection } from '@cloudscape-design/collection-hooks';
import {
  Alert,
  AreaChart,
  Box,
  Container,
  ColumnLayout,
  DateRangePicker,
  Header,
  KeyValuePairs,
  LineChart,
  Pagination,
  SpaceBetween,
  Spinner,
  Table,
  type DateRangePickerProps,
} from '@cloudscape-design/components';
import { fmtCurrency, fmtDate, fmtDateTime } from '../format';
import { usePageTitle } from '../lib/usePageTitle';
import { useNotifications } from '../notifications/useNotifications';
import { useCost, toCostSummaryDollars, toMonthRows, toHourlyEnergyPoints, type CostMonthRow } from '../hooks/useCost';
import { useUtility, toUtilityChartPoints, hasUnexpectedExport } from '../hooks/useUtility';

/**
 * Default range: the current calendar month to date. This is a "cost &
 * utility reconciliation" page, and a bill/tariff period is inherently a
 * calendar-month concept (api/src/lib/tariff.ts selects tariff vintage and
 * amortizes the fixed charge per calendar month) — defaulting to
 * month-to-date keeps the initial monthly table and summary tiles showing a
 * single, currently-accruing bill period rather than an arbitrary trailing
 * window that straddles two tariff months.
 */
function defaultRange(): DateRangePickerProps.AbsoluteValue {
  const now = new Date();
  const start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
  return {
    type: 'absolute',
    startDate: start.toISOString(),
    endDate: now.toISOString(),
  };
}

function toRangeBounds(value: DateRangePickerProps.Value | null): { from: string; to: string } {
  if (value && value.type === 'absolute') {
    return { from: value.startDate, to: value.endDate };
  }
  const fallback = defaultRange();
  return { from: fallback.startDate, to: fallback.endDate };
}

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function monthLabel(row: CostMonthRow): string {
  return `${MONTH_NAMES[row.month - 1]} ${row.year}`;
}

export default function Cost() {
  usePageTitle('Cost & Utility Reconciliation');
  const { notifyError } = useNotifications();

  const [range, setRange] = useState<DateRangePickerProps.Value | null>(defaultRange());
  const { from, to } = toRangeBounds(range);

  const costQuery = useCost(from, to);
  const utilityQuery = useUtility(from, to);

  useEffect(() => {
    if (costQuery.isError) notifyError(costQuery.error, 'load cost data');
  }, [costQuery.isError, costQuery.error, notifyError]);

  useEffect(() => {
    if (utilityQuery.isError) notifyError(utilityQuery.error, 'load utility readings');
  }, [utilityQuery.isError, utilityQuery.error, notifyError]);

  const summary = useMemo(
    () => (costQuery.data ? toCostSummaryDollars(costQuery.data) : null),
    [costQuery.data],
  );

  const monthRows = useMemo(
    () => (costQuery.data ? toMonthRows(costQuery.data.months) : []),
    [costQuery.data],
  );

  const hourlyPoints = useMemo(
    () => (costQuery.data ? toHourlyEnergyPoints(costQuery.data.byHour) : []),
    [costQuery.data],
  );

  const utilityPoints = useMemo(
    () => (utilityQuery.data ? toUtilityChartPoints(utilityQuery.data.points) : []),
    [utilityQuery.data],
  );

  const exportFlag = useMemo(
    () => (utilityQuery.data ? hasUnexpectedExport(utilityQuery.data.points) : false),
    [utilityQuery.data],
  );

  const { items, collectionProps, paginationProps } = useCollection(monthRows, {
    sorting: { defaultState: { sortingColumn: { sortingField: 'year' }, isDescending: true } },
    pagination: { pageSize: 12 },
  });

  return (
    <SpaceBetween size="l">
      <Header
        variant="h1"
        description="Reconciles metered utility import/export against the modeled tariff cost for a date range."
      >
        Cost &amp; Utility Reconciliation
      </Header>

      <Container header={<Header variant="h2">Date range</Header>}>
        <DateRangePicker
          value={range}
          onChange={({ detail }) => setRange(detail.value)}
          relativeOptions={[
            { key: 'last-30-days', amount: 30, unit: 'day', type: 'relative' },
            { key: 'last-7-days', amount: 7, unit: 'day', type: 'relative' },
          ]}
          isValidRange={(value) => {
            if (value === null || value.type !== 'absolute') {
              return { valid: true };
            }
            if (!value.startDate || !value.endDate) {
              return { valid: false, errorMessage: 'Both start and end dates are required.' };
            }
            if (new Date(value.startDate) > new Date(value.endDate)) {
              return { valid: false, errorMessage: 'Start date must be before end date.' };
            }
            return { valid: true };
          }}
          placeholder="Filter by date range"
          rangeSelectorMode="absolute-only"
          i18nStrings={{
            todayAriaLabel: 'Today',
            clearButtonLabel: 'Clear',
            cancelButtonLabel: 'Cancel',
            applyButtonLabel: 'Apply',
          }}
        />
      </Container>

      {exportFlag && (
        <Alert type="warning" header="Unexpected grid export detected">
          This site has no solar export, so metered <code>kwhReceived</code> should read ~0 throughout the
          range. Some readings in this range show a meaningfully non-zero value — that is a data-quality
          signal worth investigating, not expected behavior.
        </Alert>
      )}

      <Container
        header={
          <Header variant="h2" description="All dollar figures below cover the selected range.">
            Cost summary
          </Header>
        }
      >
        {costQuery.isLoading ? (
          <Spinner />
        ) : summary ? (
          <SpaceBetween size="l">
            <ColumnLayout columns={2} variant="text-grid">
              <KeyValuePairs
                columns={1}
                items={[
                  { label: 'Total kWh delivered', value: summary.totalKwh.toFixed(2) },
                  { label: 'Total cost (all charges)', value: fmtCurrency(summary.totalDollars) },
                ]}
              />
              <KeyValuePairs
                columns={1}
                items={[
                  {
                    type: 'group',
                    title: 'Breakdown by charge kind',
                    items: [
                      { label: 'Energy (usage-based, hourly-attributable)', value: fmtCurrency(summary.energyDollars) },
                      { label: 'Fixed monthly charge (billing-period only)', value: fmtCurrency(summary.fixedDollars) },
                      { label: 'Percent-of-bill riders — franchise fee + sales tax (billing-period only)', value: fmtCurrency(summary.percentDollars) },
                    ],
                  },
                ]}
              />
            </ColumnLayout>
            <Alert type="info" header="Why fixed and percent charges aren't in the hourly chart below">
              The fixed monthly charge and the cascading franchise-fee/sales-tax riders are billed at the
              month level, not per kWh — they cannot be split across hours without misrepresenting each
              hour&apos;s real marginal cost. They appear here and in the monthly table only, never in the
              hourly energy chart.
            </Alert>
          </SpaceBetween>
        ) : (
          <Box color="text-status-inactive">No cost data for this range.</Box>
        )}
      </Container>

      <Container header={<Header variant="h2">Monthly breakdown</Header>}>
        <Table
          {...collectionProps}
          items={items}
          pagination={<Pagination {...paginationProps} />}
          variant="container"
          columnDefinitions={[
            { id: 'month', header: 'Billing month', cell: (row) => monthLabel(row), sortingField: 'month' },
            { id: 'kwh', header: 'kWh', cell: (row) => row.kwh.toFixed(2), sortingField: 'kwh' },
            { id: 'daysPresent', header: 'Days present', cell: (row) => row.daysPresent, sortingField: 'daysPresent' },
            { id: 'energy', header: 'Energy', cell: (row) => fmtCurrency(row.energyDollars), sortingField: 'energyDollars' },
            { id: 'fixed', header: 'Fixed charge', cell: (row) => fmtCurrency(row.fixedDollars), sortingField: 'fixedDollars' },
            { id: 'percent', header: 'Franchise fee + tax', cell: (row) => fmtCurrency(row.percentDollars), sortingField: 'percentDollars' },
            { id: 'total', header: 'Total', cell: (row) => fmtCurrency(row.totalDollars), sortingField: 'totalDollars' },
          ]}
          empty={<Box textAlign="center">No billing months in this range.</Box>}
          trackBy="key"
        />
      </Container>

      <Container
        header={
          <Header variant="h2" description="Energy cost only — excludes the fixed monthly charge and percent-of-bill riders, which are billed at the month level (see summary above), not per hour.">
            Hourly energy cost
          </Header>
        }
      >
        {costQuery.isLoading ? (
          <Spinner />
        ) : (
          <LineChart
            series={[
              {
                title: 'Energy cost per hour (excludes fixed charge & riders)',
                type: 'line',
                data: hourlyPoints.map((p) => ({ x: p.x, y: p.energyDollars })),
                valueFormatter: (v) => fmtCurrency(v),
              },
            ]}
            xScaleType="time"
            xTitle="Time"
            yTitle="Energy cost ($)"
            yTickFormatter={(v) => fmtCurrency(v)}
            xTickFormatter={(v) => fmtDateTime(v as unknown as Date)}
            height={300}
            hideFilter
            empty={<Box textAlign="center">No hourly data for this range.</Box>}
          />
        )}
      </Container>

      <Container header={<Header variant="h2">Utility import vs. export</Header>}>
        {utilityQuery.isLoading ? (
          <Spinner />
        ) : (
          <AreaChart
            series={[
              {
                title: 'kWh delivered (imported from grid)',
                type: 'area',
                data: utilityPoints.map((p) => ({ x: p.x, y: p.kwhDelivered })),
              },
              {
                title: 'kWh received (exported to grid)',
                type: 'area',
                data: utilityPoints.map((p) => ({ x: p.x, y: p.kwhReceived })),
              },
            ]}
            xScaleType="time"
            xTitle="Time"
            yTitle="kWh"
            xTickFormatter={(v) => fmtDateTime(v as unknown as Date)}
            height={300}
            hideFilter
            empty={<Box textAlign="center">No utility readings for this range.</Box>}
          />
        )}
      </Container>

      <Box color="text-status-inactive" fontSize="body-s">
        Range shown: {fmtDate(from)} – {fmtDate(to)}.
      </Box>
    </SpaceBetween>
  );
}
